import { Link } from "react-router-dom";
import { ArrowLeft, FileText, CheckCircle, XCircle, AlertTriangle, Scale, CreditCard, Shield } from "lucide-react";

const LOGO_URL = "/images/logo-full.png";

export default function TermsOfService() {
  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/login" className="flex items-center gap-2 text-slate-600 hover:text-slate-900">
            <ArrowLeft className="w-4 h-4" />
            <span>Torna al login</span>
          </Link>
          <img src={LOGO_URL} alt="Affidya" className="h-8" />
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-4 py-8">
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-8">
          
          <div className="flex items-center gap-3 mb-6">
            <div className="p-3 bg-indigo-100 rounded-lg">
              <Scale className="w-6 h-6 text-indigo-600" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-slate-900">Termini e Condizioni di Utilizzo Affidya</h1>
              <p className="text-slate-500">Contratto SaaS B2B — Versione 1.0</p>
            </div>
          </div>

          <div className="mb-8 rounded-lg border border-emerald-200 bg-emerald-50 p-4 text-sm text-slate-700">
            <p className="font-medium text-emerald-900">Contratto di Utilizzo Affidya v1.0</p>
            <p className="mt-2">
              Il testo integrale del contratto SaaS è disponibile in formato PDF:{" "}
              <a
                href="/documents/legal/contratto-affidya-v1.pdf"
                target="_blank"
                rel="noopener noreferrer"
                className="font-medium text-emerald-700 hover:underline"
              >
                scarica il Contratto di Utilizzo Affidya v1.0
              </a>
              . Consulta anche la{" "}
              <Link to="/legal/privacy" className="font-medium text-emerald-700 hover:underline">Privacy Policy</Link>
              {" "}e la{" "}
              <Link to="/legal/cookies" className="font-medium text-emerald-700 hover:underline">Cookie Policy</Link>.
            </p>
          </div>

          <p className="text-sm text-slate-500 mb-8">Ultimo aggiornamento: 1 giugno 2026</p>

          {/* Premessa */}
          <div className="bg-slate-50 rounded-lg p-4 mb-8">
            <p className="text-slate-700">
              I presenti Termini e Condizioni regolano l'utilizzo della piattaforma software "Affidya"
              (di seguito "Piattaforma" o "Servizio"), un gestionale cloud SaaS B2B per strutture
              socio-assistenziali fornito da R&CO. GROUP S.r.l. (di seguito "Fornitore").
            </p>
            <p className="text-slate-700 mt-2">
              L'utilizzo della Piattaforma implica l'accettazione integrale dei presenti Termini.
            </p>
          </div>

          {/* Sezione 1 */}
          <section className="mb-8">
            <h2 className="text-lg font-semibold text-slate-900 mb-3 flex items-center gap-2">
              <FileText className="w-5 h-5 text-indigo-600" />
              1. Definizioni
            </h2>
            <ul className="text-slate-700 space-y-2">
              <li><strong>"Cliente"</strong>: la struttura socio-assistenziale che sottoscrive il servizio</li>
              <li><strong>"Utente"</strong>: qualsiasi persona fisica autorizzata dal Cliente ad accedere alla Piattaforma</li>
              <li><strong>"Account"</strong>: l'insieme di credenziali e dati che identificano un Utente</li>
              <li><strong>"Ospite"</strong>: la persona assistita dalla struttura i cui dati sono gestiti sulla Piattaforma</li>
              <li><strong>"Fascicolo"</strong>: l'insieme dei dati relativi a un Ospite</li>
            </ul>
          </section>

          {/* Sezione 2 */}
          <section className="mb-8">
            <h2 className="text-lg font-semibold text-slate-900 mb-3 flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-indigo-600" />
              2. Oggetto del Servizio
            </h2>
            <p className="text-slate-700 mb-3">
              La Piattaforma Affidya fornisce i seguenti servizi in modalità SaaS (Software as a Service):
            </p>
            <ul className="text-slate-700 space-y-1 ml-4">
              <li>• Gestione anagrafica ospiti e fascicoli assistenziali</li>
              <li>• Diario assistenziale e consegne</li>
              <li>• Piano Assistenziale Individualizzato (PAI)</li>
              <li>• Gestione documentale con upload referti</li>
              <li>• Integrazione con Alloggiati Web (Questura)</li>
              <li>• Dashboard e reportistica</li>
              <li>• Gestione personale e turni</li>
              <li>• Network "Ospiti in Rete"</li>
            </ul>
          </section>

          {/* Sezione 3 */}
          <section className="mb-8">
            <h2 className="text-lg font-semibold text-slate-900 mb-3 flex items-center gap-2">
              <CreditCard className="w-5 h-5 text-indigo-600" />
              3. Registrazione e Abbonamento
            </h2>
            <div className="space-y-4">
              <div>
                <h3 className="font-medium text-slate-900 mb-2">3.1 Registrazione</h3>
                <p className="text-slate-700 text-sm">
                  Per accedere al Servizio è necessario completare la procedura di registrazione fornendo 
                  dati veritieri e completi. Il Cliente è responsabile della veridicità delle informazioni 
                  fornite e del mantenimento della sicurezza delle credenziali di accesso.
                </p>
              </div>
              <div>
                <h3 className="font-medium text-slate-900 mb-2">3.2 Piani di Abbonamento</h3>
                <p className="text-slate-700 text-sm">
                  Il Servizio è offerto con abbonamento a pagamento. I prezzi, le funzionalità incluse 
                  e le modalità di pagamento sono specificati nella pagina di registrazione e possono 
                  variare in base al piano scelto.
                </p>
              </div>
              <div>
                <h3 className="font-medium text-slate-900 mb-2">3.3 Fatturazione</h3>
                <p className="text-slate-700 text-sm">
                  La fatturazione avviene con cadenza annuale anticipata. Il Cliente riceverà fattura 
                  elettronica all'indirizzo PEC/SDI indicato in fase di registrazione.
                </p>
              </div>
            </div>
          </section>

          {/* Sezione 4 */}
          <section className="mb-8">
            <h2 className="text-lg font-semibold text-slate-900 mb-3 flex items-center gap-2">
              <Shield className="w-5 h-5 text-indigo-600" />
              4. Obblighi del Cliente
            </h2>
            <p className="text-slate-700 mb-3">Il Cliente si impegna a:</p>
            <ul className="text-slate-700 space-y-2 ml-4">
              <li className="flex items-start gap-2">
                <CheckCircle className="w-4 h-4 text-green-500 mt-1 flex-shrink-0" />
                <span>Utilizzare la Piattaforma in conformità alle leggi vigenti</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle className="w-4 h-4 text-green-500 mt-1 flex-shrink-0" />
                <span>Garantire la legittimità del trattamento dei dati degli Ospiti</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle className="w-4 h-4 text-green-500 mt-1 flex-shrink-0" />
                <span>Custodire le credenziali di accesso e non condividerle con terzi non autorizzati</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle className="w-4 h-4 text-green-500 mt-1 flex-shrink-0" />
                <span>Notificare tempestivamente eventuali violazioni di sicurezza</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle className="w-4 h-4 text-green-500 mt-1 flex-shrink-0" />
                <span>Mantenere aggiornate le informazioni di contatto e fatturazione</span>
              </li>
            </ul>
          </section>

          {/* Sezione 5 */}
          <section className="mb-8">
            <h2 className="text-lg font-semibold text-slate-900 mb-3 flex items-center gap-2">
              <XCircle className="w-5 h-5 text-indigo-600" />
              5. Divieti
            </h2>
            <p className="text-slate-700 mb-3">È espressamente vietato:</p>
            <ul className="text-slate-700 space-y-2 ml-4">
              <li className="flex items-start gap-2">
                <XCircle className="w-4 h-4 text-red-500 mt-1 flex-shrink-0" />
                <span>Tentare di accedere a dati di altri Clienti</span>
              </li>
              <li className="flex items-start gap-2">
                <XCircle className="w-4 h-4 text-red-500 mt-1 flex-shrink-0" />
                <span>Effettuare reverse engineering o decompilazione del software</span>
              </li>
              <li className="flex items-start gap-2">
                <XCircle className="w-4 h-4 text-red-500 mt-1 flex-shrink-0" />
                <span>Utilizzare la Piattaforma per attività illecite</span>
              </li>
              <li className="flex items-start gap-2">
                <XCircle className="w-4 h-4 text-red-500 mt-1 flex-shrink-0" />
                <span>Caricare contenuti malevoli (virus, malware)</span>
              </li>
              <li className="flex items-start gap-2">
                <XCircle className="w-4 h-4 text-red-500 mt-1 flex-shrink-0" />
                <span>Cedere o sublicenziare l'accesso a terzi</span>
              </li>
            </ul>
          </section>

          {/* Sezione 6 */}
          <section className="mb-8">
            <h2 className="text-lg font-semibold text-slate-900 mb-3">6. Proprietà Intellettuale</h2>
            <p className="text-slate-700">
              La Piattaforma, inclusi software, codice, design, marchi e documentazione, è di proprietà 
              esclusiva del Fornitore. Il Cliente acquisisce esclusivamente una licenza d'uso non esclusiva, 
              non trasferibile, limitata alla durata dell'abbonamento.
            </p>
            <p className="text-slate-700 mt-2">
              I dati inseriti dal Cliente rimangono di sua esclusiva proprietà.
            </p>
          </section>

          {/* Sezione 7 */}
          <section className="mb-8">
            <h2 className="text-lg font-semibold text-slate-900 mb-3">7. Livelli di Servizio (SLA)</h2>
            <div className="bg-slate-50 rounded-lg p-4">
              <ul className="text-slate-700 space-y-2">
                <li><strong>Disponibilità:</strong> Il Fornitore si impegna a garantire una disponibilità del 99% su base mensile</li>
                <li><strong>Manutenzione:</strong> Gli interventi di manutenzione programmata saranno comunicati con almeno 48 ore di anticipo</li>
                <li><strong>Supporto:</strong> Assistenza via email in orario lavorativo (Lun-Ven 9:00-18:00)</li>
                <li><strong>Backup:</strong> Backup giornalieri con retention minima di 30 giorni</li>
              </ul>
            </div>
          </section>

          {/* Sezione 8 */}
          <section className="mb-8">
            <h2 className="text-lg font-semibold text-slate-900 mb-3 flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-indigo-600" />
              8. Limitazione di Responsabilità
            </h2>
            <div className="bg-amber-50 rounded-lg p-4 border border-amber-200">
              <p className="text-amber-900 text-sm">
                Il Fornitore non sarà responsabile per danni indiretti, incidentali o consequenziali 
                derivanti dall'uso o dall'impossibilità di utilizzare il Servizio. La responsabilità 
                complessiva del Fornitore è limitata all'importo corrisposto dal Cliente nei 12 mesi 
                precedenti l'evento dannoso.
              </p>
            </div>
            <p className="text-slate-700 text-sm mt-3">
              Il Fornitore non è responsabile per:
            </p>
            <ul className="text-slate-600 text-sm mt-2 space-y-1 ml-4">
              <li>• Perdita di dati dovuta a negligenza del Cliente</li>
              <li>• Interruzioni causate da forza maggiore</li>
              <li>• Uso improprio della Piattaforma</li>
              <li>• Contenuto dei dati inseriti dal Cliente</li>
            </ul>
          </section>

          {/* Sezione 9 */}
          <section className="mb-8">
            <h2 className="text-lg font-semibold text-slate-900 mb-3">9. Recesso e Risoluzione</h2>
            <div className="space-y-4">
              <div>
                <h3 className="font-medium text-slate-900 mb-2">9.1 Recesso del Cliente</h3>
                <p className="text-slate-700 text-sm">
                  Il Cliente può recedere dal contratto in qualsiasi momento con preavviso di 30 giorni. 
                  Non sono previsti rimborsi per periodi di abbonamento non utilizzati.
                </p>
              </div>
              <div>
                <h3 className="font-medium text-slate-900 mb-2">9.2 Risoluzione per inadempimento</h3>
                <p className="text-slate-700 text-sm">
                  Il Fornitore può sospendere o risolvere il contratto in caso di mancato pagamento, 
                  violazione dei presenti Termini o uso illecito della Piattaforma.
                </p>
              </div>
              <div>
                <h3 className="font-medium text-slate-900 mb-2">9.3 Effetti della cessazione</h3>
                <p className="text-slate-700 text-sm">
                  Alla cessazione del contratto, il Cliente potrà richiedere l'esportazione dei propri dati 
                  entro 30 giorni. Trascorso tale termine, i dati saranno cancellati secondo la policy di retention.
                </p>
              </div>
            </div>
          </section>

          {/* Sezione 10 */}
          <section className="mb-8">
            <h2 className="text-lg font-semibold text-slate-900 mb-3">10. Protezione dei Dati</h2>
            <p className="text-slate-700">
              Il trattamento dei dati personali è disciplinato dalla{" "}
              <Link to="/privacy" className="text-blue-600 hover:underline">Privacy Policy</Link> e 
              dall'eventuale Accordo sul Trattamento dei Dati (DPA) sottoscritto tra le parti.
            </p>
            <p className="text-slate-700 mt-2">
              In relazione ai dati degli Ospiti, il Cliente agisce come Titolare del trattamento e 
              il Fornitore come Responsabile del trattamento ai sensi dell'Art. 28 GDPR.
            </p>
          </section>

          {/* Sezione 11 */}
          <section className="mb-8">
            <h2 className="text-lg font-semibold text-slate-900 mb-3">11. Modifiche ai Termini</h2>
            <p className="text-slate-700">
              Il Fornitore si riserva il diritto di modificare i presenti Termini. Le modifiche saranno 
              comunicate con almeno 30 giorni di anticipo. L'uso continuato della Piattaforma dopo 
              l'entrata in vigore delle modifiche costituisce accettazione delle stesse.
            </p>
          </section>

          {/* Sezione 12 */}
          <section>
            <h2 className="text-lg font-semibold text-slate-900 mb-3">12. Legge Applicabile e Foro Competente</h2>
            <div className="bg-slate-50 rounded-lg p-4">
              <p className="text-slate-700">
                I presenti Termini sono regolati dalla legge italiana. Per qualsiasi controversia 
                sarà competente in via esclusiva il Foro di <strong>Roma</strong>.
              </p>
            </div>
          </section>

        </div>

        {/* Footer */}
        <div className="text-center mt-8 text-slate-500 text-sm">
          <p>© {new Date().getFullYear()} R&CO. GROUP S.r.l. - Tutti i diritti riservati</p>
          <div className="flex justify-center gap-4 mt-2">
            <Link to="/privacy" className="hover:text-slate-700">Privacy Policy</Link>
            <Link to="/cookies" className="hover:text-slate-700">Cookie Policy</Link>
            <Link to="/terms" className="hover:text-slate-700">Termini di Servizio</Link>
          </div>
        </div>
      </main>
    </div>
  );
}
